import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnType, TablePaginationConfig } from "antd/es/table";
import Tag from "antd/es/tag";
import { IntlShape, useIntl } from "react-intl";

import { Activity, Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { UserRef } from "@mapmycustomers/shared/types/User";
import { Avatar } from "@mapmycustomers/ui";

import AnnotationEditModal from "@app/component/activity/ActivityAnnotation/AnnotationEditModal";
import ActivityName from "@app/component/activity/ActivityName";
import Table from "@app/component/Table";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import UserPreviewCard from "@app/component/UserPreviewCard";
import MapFilter from "@app/scene/reports/enums/MapFilter";
import { DEFAULT_ENTITY_LIST_PAGE_SIZE } from "@app/scene/reports/utils/const";
import { showEntityView } from "@app/store/entityView/actions";
import { formatRawDate } from "@app/util/formatters";

import styles from "./BasePinCard.module.scss";

const useColumns = (
  intl: IntlShape,
  filter: MapFilter,
  onPreview: typeof showEntityView
): ColumnType<Activity>[] => {
  const handleCompanyClick = useCallback(
    (companyId: Company["id"]) =>
      onPreview({ entityId: companyId, entityType: EntityType.COMPANY }),
    [onPreview]
  );
  const handleDealClick = useCallback(
    (dealId: Deal["id"]) => onPreview({ entityId: dealId, entityType: EntityType.DEAL }),
    [onPreview]
  );
  const handlePersonClick = useCallback(
    (personId: Person["id"]) => onPreview({ entityId: personId, entityType: EntityType.PERSON }),
    [onPreview]
  );
  return useMemo(
    () => [
      {
        dataIndex: "name",
        key: "name",
        render: (_: any, activity: Activity) => (
          <ActivityName
            activity={activity}
            onCompanyClick={handleCompanyClick}
            onDealClick={handleDealClick}
            onPersonClick={handlePersonClick}
          />
        ),
        title: intl.formatMessage({
          id: "reports.activity.activitiesList.column.activity",
          defaultMessage: "Activity",
          description: "Title of a Activity column in Activity table on reports page",
        }),
      },
      {
        dataIndex: "note",
        key: "note",
        render: (note: string) => <LongContentParagraph rows={3}>{note}</LongContentParagraph>,
        title: intl.formatMessage({
          id: "reports.activity.activitiesList.column.note",
          defaultMessage: "Note",
          description: "Title of a Note column in Activity table on reports page",
        }),
        width: "30%",
      },
      {
        dataIndex: filter === MapFilter.COMPLETED_ACTIVITIES ? "completedAt" : "startAt",
        key: filter === MapFilter.COMPLETED_ACTIVITIES ? "completedAt" : "startAt",
        render: (date: string) => (
          <Tag
            className={styles.tag}
            color={filter === MapFilter.OVERDUE_ACTIVITIES ? "error" : "success"}
          >
            {formatRawDate(date, "PPp")}
          </Tag>
        ),
        title:
          filter === MapFilter.COMPLETED_ACTIVITIES
            ? intl.formatMessage({
                id: "reports.activity.activitiesList.column.completedOn",
                defaultMessage: "Done On",
                description: "Title of a Done On column in Activity table on reports page",
              })
            : intl.formatMessage({
                id: "reports.activity.activitiesList.column.dueOn",
                defaultMessage: "Due On",
                description: "Title of a Due On column in Activity table on reports page",
              }),
      },
      {
        dataIndex: "assignee",
        key: "assignee",
        render: (assignee: UserRef, activity: Activity) =>
          !assignee || assignee?.id === activity.user.id ? (
            <UserPreviewCard userId={activity.user.id}>
              <Avatar actionOnHover className={styles.avatar} user={activity.user} />
            </UserPreviewCard>
          ) : (
            <div>
              <UserPreviewCard userId={activity.user.id}>
                <Avatar actionOnHover className={styles.avatar} user={activity.user} />
              </UserPreviewCard>
              <FontAwesomeIcon className={styles.chevron} icon={faChevronRight} />
              <UserPreviewCard userId={assignee.id}>
                <Avatar actionOnHover className={styles.avatar} user={assignee} />
              </UserPreviewCard>
            </div>
          ),
        title: intl.formatMessage({
          id: "reports.activity.activitiesList.column.assignee",
          defaultMessage: "Assigned to",
          description: "Title of a Assigned to column in Activity table on reports page",
        }),
      },
    ],
    [filter, intl, handleCompanyClick, handleDealClick, handlePersonClick]
  );
};

interface Props {
  activities: Activity[];
  filter: MapFilter;
  onLoadData?: (page: number) => void;
  page: number;
  showEntityView: typeof showEntityView;
  total: number;
}

const ActivityList: React.FC<Props> = ({
  activities,
  filter,
  onLoadData,
  page,
  showEntityView,
  total,
}) => {
  const intl = useIntl();
  const [selectedActivity, selectActivity] = useState<Activity | undefined>(undefined);
  const [annotationElement, setAnnotationElement] = useState<HTMLElement | undefined>();
  const columns = useColumns(intl, filter, showEntityView);
  const rowEventHandlers = useCallback(
    (record: Activity) => ({
      onClick: ({ currentTarget }: React.MouseEvent<HTMLElement, MouseEvent>) => {
        selectActivity(record);
        setAnnotationElement(currentTarget);
      },
    }),
    [selectActivity, setAnnotationElement]
  );

  const handleReloadList = useCallback(() => {
    onLoadData?.(page);
  }, [onLoadData, page]);

  const paginationOptions: TablePaginationConfig = useMemo(
    () => ({
      current: page + 1,
      defaultPageSize: DEFAULT_ENTITY_LIST_PAGE_SIZE,
      onChange: (page) => onLoadData?.(page - 1),
      position: ["bottomCenter"],
      showSizeChanger: false,
      total,
    }),
    [page, onLoadData, total]
  );

  return (
    <div className={styles.tableContainer}>
      <Table<Activity>
        clickableRows
        columns={columns}
        dataSource={activities}
        onRow={rowEventHandlers}
        pagination={paginationOptions}
        rowKey="id"
        showBorder
      />
      {selectedActivity && (
        <AnnotationEditModal
          activityId={selectedActivity.id}
          element={annotationElement}
          onChange={handleReloadList}
          onHide={() => selectActivity(undefined)}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(ActivityList);
